'use client'

import type { ComponentProps, ReactNode } from 'react'
import type { InputProps as AriaInputProps } from 'react-aria-components'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import type { SharedUITextInputProps } from '../../_shared/SharedUITextInput.client'
import { SharedUITextInput } from '../../_shared/SharedUITextInput.client'

type TextInputRenderContext = Parameters<
  NonNullable<ComponentProps<typeof AbstractInput>['render']>
>[0]

type TextInputProps = SharedUITextInputProps<
  'maxLength' | 'minLength' | 'required' | 'pattern' | 'asyncValidate'
> & {
  children?: ReactNode | ((context: TextInputRenderContext) => ReactNode)
  isDisabled?: boolean
  isLoading?: boolean
  maxLength?: number
  readOnly?: boolean
  value?: string
  validate?: (value: string) => string | undefined
} & StrictOmit<AriaInputProps, 'type' | 'children'>

export const textInputSizes = {
  sm: '[&_input]:h-4 [&_input]:rounded-xs [--pl:0.5rem] [--pr:0.5rem]',
  md: '[&_input]:h-5 [&_input]:rounded-sm [--pl:1rem] [--pr:1rem]',
}

export function TextInput({
  name,
  label,
  placeholder,
  rightButton,
  className,
  classNameInput,
  autocomplete,
  requiredErrorMessage,
  readOnly,
  type = 'text',
  maxLength,
  isDisabled = false,
  onChange,
  onBlur,
  onFocus,
  value,
  trailingOutlet,
  isLoading,
  asyncValidate,
  validate,
  minLength,
  pattern,
  required,
  children,
  ...props
}: TextInputProps) {
  return (
    <AbstractInput
      name={name}
      requiredErrorMessage={requiredErrorMessage}
      validate={validate ? { textInputValidate: validate } : undefined}
      asyncValidate={asyncValidate}
      minLength={minLength}
      pattern={pattern}
      required={required}
      render={({ field, fieldState, formState, fieldHelpers }) => (
        <SharedUITextInput
          {...field}
          {...props}
          className={className}
          classNameInput={classNameInput}
          type={type}
          placeholder={placeholder}
          label={label}
          rightButton={rightButton}
          isDisabled={isDisabled}
          maxLength={maxLength}
          readOnly={readOnly}
          isSubmitting={formState.isSubmitting}
          isRequired={fieldState.isRequired}
          isLoading={isLoading || fieldState.isValidating}
          error={fieldState.error}
          autocomplete={autocomplete}
          onChange={(e) => {
            onChange?.(e)
            field.onChange(e.target.value)
          }}
          onBlur={(e) => {
            onBlur?.(e)

            if (e.target.value !== field.value) {
              field.onChange(e.target.value)
            }

            field.onBlur()
          }}
          value={value ?? field.value}
          onFocus={onFocus}
          trailingOutlet={trailingOutlet}
        >
          {typeof children === 'function'
            ? children({ field, fieldState, formState, fieldHelpers })
            : children}
        </SharedUITextInput>
      )}
    />
  )
}
